import AIGeneratedLabel from "@/components/landing/blog/AIGeneratedLabel";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { getRelatedPosts } from "@/data/blogPosts";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/hooks/useLanguage";

interface BlogRelatedArticlesProps {
  currentSlug: string;
}

const BlogRelatedArticles = ({ currentSlug }: BlogRelatedArticlesProps) => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, localizePath, language } = useLanguage();
  const relatedPosts = getRelatedPosts(currentSlug, 3, language);

  if (relatedPosts.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="py-16 md:py-24 bg-secondary/30"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <Link
            to={localizePath("/blog")}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>{t("blogPage.backToOverview")}</span>
          </Link>

          <div className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-primary">
              {t("blogPage.relatedArticles")}
            </h2>
            <p className="text-muted-foreground mt-2">
              {t("blogPage.relatedIntro")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedPosts.map((post, index) => (
              <Link
                key={post.slug}
                to={localizePath(`/blog/${post.slug}`)}
                className={cn(
                  "group bg-background rounded-2xl border border-border overflow-hidden hover:border-chathero-mint/50 hover:shadow-lg transition-all duration-500",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
                style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms" }}
              >
                <div className="relative aspect-video bg-secondary/50 overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
<AIGeneratedLabel slug={post.slug} />
                </div>

                <div className="p-5">
                  <span className="inline-block text-xs font-medium text-chathero-mint bg-chathero-mint/10 px-2 py-1 rounded-full mb-3">
                    {post.category}
                  </span>

                  <h3 className="font-semibold text-primary group-hover:text-chathero-mint transition-colors line-clamp-2 mb-2">
                    {post.title}
                  </h3>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readingTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to={localizePath("/blog")}
              className="inline-flex items-center gap-2 text-chathero-mint font-semibold hover:underline group"
            >
              <span>{t("blogPage.viewAll")}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogRelatedArticles;
